<div class="container">

  <header class="page-heading">
    <h1>PPF Calculator</h1>
    <p>Calculate your PPF (Public Provident Fund) maturity value, total interest earned and year-wise growth with this online calculator for India.</p>
  </header>

  <div class="card mb-lg">
    <div class="section-title">PPF Details</div>

    <div class="field-row-2">
      <div class="field-group" [class.has-error]="hasError('yearlyInvestment')">
        <label class="field-label" for="yearlyInvestment">Yearly Investment</label>
        <div class="input-shell prefix">
          <span class="affix">₹</span>
          <input id="yearlyInvestment" type="number"
            [(ngModel)]="yearlyInvestment"
            [min]="C.yearlyInvestment.min" [max]="C.yearlyInvestment.max" [step]="C.yearlyInvestment.step"
            (ngModelChange)="onFieldChange('yearlyInvestment')"
            (blur)="onFieldBlur('yearlyInvestment')"
            placeholder="e.g. 1,50,000"
            autocomplete="off"/>
        </div>
        <div class="field-hint">{{ C.yearlyInvestment.min | currency:'INR':'symbol':'1.0-0':'en-IN' }} – {{ C.yearlyInvestment.max | currency:'INR':'symbol':'1.0-0':'en-IN' }} per year</div>
        @if (hasError('yearlyInvestment')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('yearlyInvestment') }}
          </div>
        }
      </div>

      <div class="field-group" [class.has-error]="hasError('annualRate')">
        <label class="field-label" for="annualRate">Interest Rate</label>
        <div class="input-shell suffix">
          <input id="annualRate" type="number"
            [(ngModel)]="annualRate"
            [min]="C.rate.min" [max]="C.rate.max" [step]="C.rate.step"
            (ngModelChange)="onFieldChange('annualRate')"
            (blur)="onFieldBlur('annualRate')"
            placeholder="e.g. 7.1"
            autocomplete="off"/>
          <span class="affix right">%</span>
        </div>
        <div class="field-hint">Current PPF rate is set by the government each quarter</div>
        @if (hasError('annualRate')) {
          <div class="field-error">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ fieldError('annualRate') }}
          </div>
        }
      </div>
    </div>

    <div class="field-group" [class.has-error]="hasError('tenureYears')">
      <label class="field-label" for="tenureYears">Tenure</label>
      <div class="input-shell suffix">
        <input id="tenureYears" type="number"
          [(ngModel)]="tenureYears"
          [min]="C.tenureYears.min" [max]="C.tenureYears.max" [step]="C.tenureYears.step"
          (ngModelChange)="onFieldChange('tenureYears')"
          (blur)="onFieldBlur('tenureYears')"
          placeholder="e.g. 15"
          autocomplete="off"/>
        <span class="affix right">Yr</span>
      </div>
      <div class="field-hint">PPF has a 15-year lock-in, extendable in blocks of 5 years</div>
      @if (hasError('tenureYears')) {
        <div class="field-error">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {{ fieldError('tenureYears') }}
        </div>
      }
    </div>

    <div class="action-row">
      <button type="button" class="btn btn-primary" (click)="calculate()" [class.btn-disabled]="showFormErrors && !isFormValid">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        Calculate
      </button>
      @if (calculated) {
        <button type="button" class="btn btn-secondary" (click)="reset()">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
          Reset
        </button>
      }
      @if (showFormErrors && !isFormValid) {
        <span class="form-error-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          Fix the errors above to calculate
        </span>
      }
    </div>
  </div>

  @if (calculated && result) {
    <div class="summary-grid mb-md">
      <div class="summary-card c-blue">
        <div class="sc-icon"></div>
        <div class="sc-label">Maturity Value</div>
        <div class="sc-value">{{ fmt.currency(result.maturityValue) }}</div>
        <div class="sc-sub">after {{ tenureYears }} years</div>
      </div>
      <div class="summary-card c-green">
        <div class="sc-icon"></div>
        <div class="sc-label">Total Invested</div>
        <div class="sc-value">{{ fmt.currency(result.totalInvested) }}</div>
        <div class="sc-sub">{{ fmt.currency(yearlyInvestment) }}/yr</div>
      </div>
      <div class="summary-card c-red">
        <div class="sc-icon"></div>
        <div class="sc-label">Interest Earned</div>
        <div class="sc-value">{{ fmt.currency(result.totalInterest) }}</div>
        <div class="sc-sub">@ {{ annualRate }}% p.a.</div>
      </div>
    </div>

    <div class="card charts-card mb-md">
      <div class="section-title">Visual Breakdown</div>
      <div class="charts-top-row">
        <div class="chart-panel">
          <div class="chart-panel-label">Invested vs Interest</div>
          <div class="chart-canvas-wrap chart-canvas-pie">
            <canvas #breakdownRef></canvas>
          </div>
        </div>
        <div class="chart-panel chart-panel-bar">
          <div class="chart-panel-label">Balance Growth</div>
          <div class="chart-canvas-wrap">
            <canvas #growthRef></canvas>
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-md">
      <div class="section-title">Year-wise Growth</div>
      <div class="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th class="t-left">Year</th>
              <th class="t-balance">Opening Balance</th>
              <th>Deposit</th>
              <th class="t-interest">Interest</th>
              <th class="t-balance">Closing Balance</th>
            </tr>
          </thead>
          <tbody>
            @for (row of result.rows; track row.year) {
              <tr>
                <td class="t-left t-year">Year {{ row.year }}</td>
                <td class="t-balance">{{ fmt.currency(row.openingBalance) }}</td>
                <td>{{ fmt.currency(row.deposit) }}</td>
                <td class="t-interest">{{ fmt.currency(row.interest) }}</td>
                <td class="t-balance">{{ fmt.currency(row.closingBalance) }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- ══ SEO CONTENT ══════════════════════════════════════════════════════ -->
<section class="container seo-content">
  <div class="card">
    <h2 class="section-title">How is PPF Maturity Value Calculated?</h2>
    <p>
      PPF (Public Provident Fund) compounds interest annually. This calculator uses the standard
      deposit-at-start-of-year model: each year's opening balance plus that year's deposit earns a
      full year of interest, and the resulting closing balance becomes next year's opening balance.
    </p>
    <p class="emi-formula">Balance<sub>n</sub> = (Balance<sub>n&minus;1</sub> + Yearly Deposit) &times; (1 + Rate)</p>
    <p>
      For example, depositing &#8377;1,50,000 every year at 7.1% for 15 years grows to a maturity
      value of about &#8377;40.68 lakh — of which &#8377;22.5 lakh is your own investment and the
      rest is interest earned.
    </p>

    <h2 class="section-title mt-sm">Frequently Asked Questions</h2>
    <div class="faq-item">
      <h3>What is the PPF lock-in period?</h3>
      <p>
        A PPF account has a 15-year lock-in period. After maturity, it can be extended indefinitely
        in blocks of 5 years, with or without making further contributions.
      </p>
    </div>
    <div class="faq-item">
      <h3>Is PPF interest taxable?</h3>
      <p>
        No. PPF falls under the EEE (Exempt-Exempt-Exempt) tax category — contributions,
        interest earned, and the maturity amount are all tax-free under current Indian tax law.
      </p>
    </div>
    <div class="faq-item">
      <h3>What is the minimum and maximum PPF deposit?</h3>
      <p>
        You must deposit at least &#8377;500 per year to keep a PPF account active, and can deposit
        up to &#8377;1,50,000 per year, which also qualifies for tax deduction under Section 80C.
      </p>
    </div>
    <p class="learn-crosslink">
      Read more: <a routerLink="/learn/fd-vs-rd-vs-ppf">FD vs RD vs PPF: where should each rupee go?</a>
    </p>
  </div>
</section>
